/**
 * CORE-831 — background-audio volume policy (pure).
 *
 * The ambient soundtrack (the "background audio" this ticket gives its own
 * control centre) sits underneath the room's foreground — the practice voice
 * and the live host. CORE-756 kept it there with a single hard-wired
 * `BACKGROUND_VOLUME_FACTOR = 0.35` multiplier applied inline in
 * `useRoomMediaPlayer`. CORE-831 requires that "coefficient" to be
 * configurable rather than baked into the UI, and adds a second, purely-local
 * knob: a participant may lower or fully mute the bed *for themselves* (тише /
 * выключить / вернуть) without touching the room master slider — which stays
 * exactly what the host set and is shared with everyone else.
 *
 * Splitting the rule into a pure function keeps it unit-testable without a
 * media element and gives the (still to land) background-audio panel and the
 * hook one shared definition of "how loud is the bed", exactly as
 * `roomReanchorPolicy` / `roomEffectiveDurationPolicy` do for their rules.
 *
 * Out of scope here (blocked on the backend fields approved as variant A on the
 * ticket — a host-set background master volume synced room-wide, and the
 * during-practice auto-duck that saves/restores that master): those layer *on
 * top* of this function once `WatchParty` carries the background state. Until
 * then `masterVolume` is the room master and `subordinationFactor` defaults to
 * the CORE-756 level, so the bed a participant hears is unchanged.
 */

/**
 * CORE-756 — the bed is rendered at a fixed fraction of the master volume so it
 * stays clearly subordinate to the narration / host. 0.35 keeps it present and
 * audible without competing with the foreground. CORE-831 exposes it here so it
 * is configurable rather than a magic number inside the hook.
 */
export const BACKGROUND_VOLUME_FACTOR = 0.35;

/**
 * CORE-831 QA return (Veronika, 10.08.2026) — «фон не слышен авторизованным,
 * слышен гостям».
 *
 * The bed must NOT be governed by the room's *general* volume slider. That
 * slider is persisted per browser (`agape_volume`) and also scales the Agora
 * remote voices; a returning (authorized) tester carries a value they lowered in
 * an earlier session, so `volume × 0.35` dropped the bed below audibility, while
 * a fresh anonymous guest at the default 1.0 heard it. The reported
 * «authorized-vs-guest» split is really «returning browser vs empty storage».
 * The product rule is explicit — «Не влияет на общую громкость эфира»: the bed
 * is a separate entity.
 *
 * Until the host-set background master (BE variant A) ships and is synced
 * room-wide, the bed master is full and identical for every participant
 * regardless of entry type. When that field lands, replace this constant with
 * the synced host value — keep the bed off the room general slider.
 */
export const DEFAULT_BACKGROUND_MASTER_VOLUME = 1;

export interface BackgroundVolumeInput {
    /** 0..1 room master slider — the shared, host-owned level. Never mutated
     *  by the background controls. */
    masterVolume: number;
    /** 0..1 this-device-only multiplier the participant applies to the bed
     *  (1 = untouched, 0 = muted locally). Persisted via `storage.ts`, never
     *  sent to the room — it must not affect the host or other participants. */
    localMultiplier: number;
    /** 0..1 subordination factor keeping the bed under the foreground.
     *  Defaults to `BACKGROUND_VOLUME_FACTOR`; configurable per the ticket. */
    subordinationFactor?: number;
}

const clamp01 = (n: number): number => {
    if (!Number.isFinite(n)) return 0;
    if (n < 0) return 0;
    if (n > 1) return 1;
    return n;
};

/**
 * Effective volume (0..1) for the ambient background element. Every input is
 * clamped into [0, 1] first so a stray value (a bad slider read, a corrupt
 * stored multiplier) can never overdrive the HTMLMediaElement.
 */
export function computeBackgroundVolume({
    masterVolume,
    localMultiplier,
    subordinationFactor = BACKGROUND_VOLUME_FACTOR,
}: BackgroundVolumeInput): number {
    return clamp01(
        clamp01(masterVolume) * clamp01(localMultiplier) * clamp01(subordinationFactor),
    );
}

/**
 * CORE-831 — the volume the bed `<audio>` element should actually carry for a
 * participant. Deliberately takes NO room-general-volume input: the bed master
 * is the (host-owned) background master, not the room slider — see
 * `DEFAULT_BACKGROUND_MASTER_VOLUME`. Keeping the room slider *out of this
 * signature* is what fixes the QA return and stops anyone re-coupling the two by
 * accident: to depend on the room slider again you would have to change this
 * function, and the test that pins it.
 *
 * Bed = backgroundMaster × this-device localMultiplier × subordination.
 */
export function computeBackgroundElementVolume(
    localMultiplier: number,
    subordinationFactor: number = BACKGROUND_VOLUME_FACTOR,
    backgroundMasterVolume: number = DEFAULT_BACKGROUND_MASTER_VOLUME,
): number {
    return computeBackgroundVolume({
        masterVolume: backgroundMasterVolume,
        localMultiplier,
        subordinationFactor,
    });
}

/**
 * CORE-831 — legacy helper for older callers that still model a global room mute
 * as a switch over the bed. The live-room dock now exposes its own background
 * audio toggle and uses `computeBackgroundElementVolume(backgroundLocalVolume)`
 * directly, so video sound and background audio stay independent.
 *
 * Mute is treated as a SWITCH, not a level: at `roomVolume === 0` the bed goes
 * silent with the rest of the room, and at every other value the bed keeps its
 * own level — which is what the QA return and the product rule («Не влияет на
 * общую громкость эфира») actually require. Scaling by the slider is what was
 * broken; honouring a full mute is not the same thing.
 *
 * Keep this helper for compatibility with tests / older surfaces that still need
 * the explicit "room muted means bed muted too" rule.
 */
export function computeBedVolumeWithRoomMute(
    roomVolume: number,
    localMultiplier: number,
): number {
    if (clamp01(roomVolume) === 0) return 0;
    return computeBackgroundElementVolume(localMultiplier);
}

/**
 * CORE-831 — коэффициент приглушения фона на время практики. Тикет требует
 * «пониженный уровень» и отдельно оговаривает, что коэффициент должен быть
 * конфигурируемым, а не захардкоженным в интерфейсе, — поэтому он живёт здесь,
 * рядом с subordination, а не числом внутри панели.
 */
export const BACKGROUND_DUCK_FACTOR = 0.4;

/**
 * CORE-831 — громкость фонового элемента на ЭФИРЕ (Live). Отличается от
 * комнатной версии одним множителем — приглушением на время практики.
 *
 * Приглушение сделано множителем поверх мастер-громкости, а НЕ записью
 * пониженного значения на сервер с последующим восстановлением. Это прямое
 * следствие правила из тикета: «ручное изменение громкости мастером во время
 * практики учитывается и не должно приводить к скачкам после её окончания».
 * Если бы приглушение писало на сервер, восстановление затёрло бы то, что
 * мастер выставил руками, пока практика играла. Множителем же мастер-громкость
 * всё время остаётся тем, что выбрал мастер, а снятие приглушения — просто
 * возврат множителя к единице, без скачка.
 *
 * Локальный множитель зрителя остаётся на его устройстве и на других не влияет.
 */
export function computeLiveBackgroundVolume(input: {
    /** 0..1 мастер-громкость фона, общая для эфира (с сервера). */
    backgroundMasterVolume: number;
    /** 0..1 личный множитель этого устройства. */
    localMultiplier: number;
    /** Практика играет прямо сейчас — фон уходит под неё. */
    ducked: boolean;
    subordinationFactor?: number;
    duckFactor?: number;
}): number {
    const {
        backgroundMasterVolume,
        localMultiplier,
        ducked,
        subordinationFactor = BACKGROUND_VOLUME_FACTOR,
        duckFactor = BACKGROUND_DUCK_FACTOR,
    } = input;
    const base = computeBackgroundVolume({
        masterVolume: backgroundMasterVolume,
        localMultiplier,
        subordinationFactor,
    });
    return ducked ? clamp01(base * clamp01(duckFactor)) : base;
}

/**
 * CORE-831 (возврат QA 26.09) — приглушать ли фон КОМНАТЫ прямо сейчас.
 *
 * На эфире флаг `ducked` проведён (`MasterLivePublisher`, `LiveDetailScreen`),
 * а комната считала громкость фона без него: фон звучал одинаково с практикой
 * на сцене и без неё. Правило то же — фон уходит под практику, пока она
 * действительно звучит:
 * - практика на сцене (`hasPractice`: у комнаты есть ролик; «убрать со сцены»
 *   переводит комнату в SelfGuided, и ролика нет);
 * - не на паузе (`isPlaying`);
 * - идёт фаза практики — отсчёт, рефлексия и конец фон не глушат.
 *
 * Считает каждый клиент сам по общему состоянию комнаты, на сервер ничего не
 * пишется — поэтому ручная громкость ведущего во время практики не скачет
 * после её окончания (см. `computeLiveBackgroundVolume`).
 */
export function shouldDuckRoomBackground(input: {
    hasPractice: boolean;
    isPlaying: boolean;
    sessionPhase: string;
}): boolean {
    return input.hasPractice && input.isPlaying && input.sessionPhase === 'practice';
}
